import { useEffect, useMemo, useRef, useState, type ChangeEvent, type ReactNode } from 'react';
import { CffAsyncTypeahead, type CffAsyncOption } from './CffAsyncTypeahead';
import { fetchDemCustomers } from '../../lib/demCustomers';
import {
  extractCitDepositInvoiceFromFile,
  mapDepositExtractToCitLines,
} from '../../lib/citDepositExtract';
import { searchCitPowerBiItems } from '../../lib/citItems';
import {
  citOffsetOverPaidItems,
  deriveCitAutoStatus,
  formatCitAmount,
  formatCitQty,
  isCitCreditCancelled,
  sumOffsetQtyForItem,
  sumPaidQtyForItem,
} from '../../lib/citList';
import {
  CIT_ALLOW_LOCKED_OFFSET_FILE_UPLOAD,
  CIT_ENFORCE_MANDATORY,
  CIT_REQUIRE_OFFSET_SUPPORTING_FILE,
  CIT_REQUIRE_PAYMENT_SUPPORTING_FILE,
  CIT_REQUIRE_SUPPORTING_FILES,
  citHasSalesConfirmationAttachment,
  citShowClearingInvoices,
  citShowDepositSections,
} from '../../lib/citSubmissions';
import type { DemCustomer, DemItem } from '../../types/dem';
import {
  CIT_STATUS_OPTIONS,
  createCitClearingInvoiceRow,
  createCitDepositLine,
  createCitDocumentRow,
  createCitItemRow,
  createCitLinkedReceipt,
  createCitOffsetLine,
  createCitOffsetRow,
} from '../../types/applicationForm';
import type {
  CitAttachmentRef,
  CitClearingInvoiceRow,
  CitDepositLine,
  CitDocumentRow,
  CitItemRow,
  CitOffsetLine,
  CitOffsetRow,
  CitTransferStatus,
  CustomiseItemsTransferFormData,
} from '../../types/applicationForm';

export type CitPendingFiles = {
  sales_confirmation: File | null;
  /** key = deposit / offset / clearing row id */
  deposit: Record<string, File | null>;
  payment: Record<string, File | null>;
  offset: Record<string, File | null>;
  clearing: Record<string, File | null>;
};

export type CitCommittedIds = {
  items: string[];
  deposit: string[];
  offset: string[];
  clearing: string[];
};

export type CitSectionLocks = {
  general: boolean;
  deposit: boolean;
  offset: boolean;
  clearing: boolean;
};

export const EMPTY_CIT_PENDING_FILES: CitPendingFiles = {
  sales_confirmation: null,
  deposit: {},
  payment: {},
  offset: {},
  clearing: {},
};

export const EMPTY_CIT_COMMITTED_IDS: CitCommittedIds = {
  items: [],
  deposit: [],
  offset: [],
  clearing: [],
};

type Props = {
  value: CustomiseItemsTransferFormData;
  onChange: (next: CustomiseItemsTransferFormData) => void;
  pendingFiles: CitPendingFiles;
  onPendingFilesChange: (next: CitPendingFiles) => void;
  disabled?: boolean;
  sectionLocks?: CitSectionLocks;
  committedIds?: CitCommittedIds;
  onRequestEditSection?: (section: keyof CitSectionLocks) => void;
  onCancelEditSection?: (section: keyof CitSectionLocks) => void;
  onOpenAttachment?: (ref: CitAttachmentRef) => void;
  /** After save: create stock-transfer PDF for one offset pair. */
  onCreateStockTransferForm?: (offsetId: string) => void | Promise<void>;
  creatingStockTransferOffsetId?: string | null;
};

function customerFromMeta(meta: unknown): DemCustomer | null {
  if (!meta || typeof meta !== 'object') return null;
  const row = meta as DemCustomer;
  if (!row.code) return null;
  return row;
}

function itemFromMeta(meta: unknown): DemItem | null {
  if (!meta || typeof meta !== 'object') return null;
  const row = meta as DemItem;
  if (!row.code) return null;
  return row;
}

function inputClass(readOnly: boolean, extra?: string): string {
  const base = readOnly ? 'field-input field-input--readonly' : 'field-input';
  return extra ? `${base} ${extra}` : base;
}

async function searchCustomers(query: string): Promise<CffAsyncOption[]> {
  const { rows } = await fetchDemCustomers({
    search: query,
    page: 1,
    pageSize: 25,
    statusFilter: 'active',
    blockedFilter: 'unblocked',
  });
  return rows.map((row) => ({
    value: row.code,
    label: `${row.code} — ${row.name}`,
    meta: row,
  }));
}

async function searchItems(query: string): Promise<CffAsyncOption[]> {
  const rows = await searchCitPowerBiItems(query, 25);
  return rows.map((row) => ({
    value: row.code,
    label: `${row.code} — ${row.description}`,
    meta: row,
  }));
}

function isCommitted(id: string, ids: string[] | undefined): boolean {
  return Boolean(ids?.includes(id));
}

function FileNameLine({
  saved,
  pending,
  onOpenAttachment,
  linkLabel,
}: {
  saved?: CitAttachmentRef;
  pending: File | null;
  onOpenAttachment?: (ref: CitAttachmentRef) => void;
  /** When set, use this instead of the stored file name as the link text. */
  linkLabel?: string;
}) {
  const label = (linkLabel ?? saved?.fileName ?? '').trim();
  return (
    <p className="field-hint cit-form__file-name">
      {pending ? (
        `Selected: ${pending.name}`
      ) : saved ? (
        onOpenAttachment && label ? (
          <button
            type="button"
            className="cit-form__file-link"
            onClick={() => onOpenAttachment(saved)}
            title={saved.fileName}
          >
            {label}
          </button>
        ) : (
          label || saved.fileName
        )
      ) : (
        'No file'
      )}
    </p>
  );
}

/** Number field (+ optional Upload beside it, like SC): view opens attachment using typed value as link text. */
function LinkedDocNumber({
  value,
  onChange,
  disabled,
  readOnly,
  placeholder,
  saved,
  pending = null,
  onPick,
  onClearPending,
  uploadDisabled,
  onOpenAttachment,
  ariaLabel,
}: {
  value: string;
  onChange?: (next: string) => void;
  disabled?: boolean;
  readOnly?: boolean;
  placeholder?: string;
  saved?: CitAttachmentRef;
  pending?: File | null;
  onPick?: (file: File | null) => void;
  onClearPending?: () => void;
  /** When set, controls only the Upload button (number field still uses `disabled`). */
  uploadDisabled?: boolean;
  onOpenAttachment?: (ref: CitAttachmentRef) => void;
  ariaLabel?: string;
}) {
  const trimmed = value.trim();
  const canOpen = Boolean(saved && onOpenAttachment && trimmed);
  const pickerDisabled = uploadDisabled ?? Boolean(disabled);
  const showUpload = Boolean(onPick && onClearPending && !pickerDisabled);

  const numberControl =
    disabled && canOpen ? (
      <button
        type="button"
        className={`${inputClass(true, 'mono')} cit-form__value-link`}
        onClick={() => onOpenAttachment!(saved!)}
        title={saved!.fileName}
        aria-label={ariaLabel}
      >
        {trimmed}
      </button>
    ) : (
      <input
        className={inputClass(Boolean(disabled || readOnly), 'mono')}
        value={value}
        onChange={(e) => onChange?.(e.target.value)}
        disabled={disabled}
        readOnly={readOnly}
        placeholder={placeholder}
        aria-label={ariaLabel}
      />
    );

  return (
    <>
      {showUpload ? (
        <div className="cit-form__sc-combo">
          {numberControl}
          <FilePicker
            saved={saved}
            pending={pending}
            onPick={onPick!}
            onClearPending={onClearPending!}
            compact
            hideLink
          />
        </div>
      ) : (
        numberControl
      )}
      {pending ? (
        <p className="field-hint cit-form__file-name">Selected: {pending.name}</p>
      ) : null}
      {!disabled && canOpen ? (
        <p className="field-hint cit-form__file-name">
          <button
            type="button"
            className="cit-form__file-link"
            onClick={() => onOpenAttachment!(saved!)}
            title={saved!.fileName}
          >
            {trimmed}
          </button>
        </p>
      ) : null}
    </>
  );
}

function ReadonlyValue({
  value,
  placeholder = '—',
  mono,
  className,
}: {
  value: string;
  placeholder?: string;
  mono?: boolean;
  className?: string;
}) {
  const text = value.trim();
  return (
    <span
      className={`cit-form__readonly-value${mono ? ' mono' : ''}${className ? ` ${className}` : ''}${
        text ? '' : ' is-empty'
      }`}
    >
      {text || placeholder}
    </span>
  );
}

function AmountField({
  value,
  onChange,
  disabled,
  placeholder = 'Amount',
}: {
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  placeholder?: string;
}) {
  if (disabled) {
    return (
      <input
        className={inputClass(true)}
        value={value.trim() ? formatCitAmount(value) : ''}
        disabled
        readOnly
        placeholder={placeholder}
        aria-label="Invoice amount"
      />
    );
  }
  return (
    <input
      className={inputClass(false)}
      type="number"
      min="0"
      step="any"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      aria-label="Invoice amount"
    />
  );
}

function QtyWithUom({
  children,
  uom,
}: {
  children: ReactNode;
  uom?: string;
}) {
  const unit = (uom ?? '').trim();
  return (
    <div className="cit-form__qty-with-uom">
      {children}
      {unit ? <span className="cit-form__uom">{unit}</span> : null}
    </div>
  );
}

function uomForItemCode(itemUomByCode: Record<string, string>, itemNo: string): string {
  return itemUomByCode[itemNo.trim().toUpperCase()] ?? '';
}

function FilePicker({
  saved,
  pending,
  onPick,
  onClearPending,
  disabled,
  onOpenAttachment,
  compact,
  hideLink,
}: {
  saved?: CitAttachmentRef;
  pending: File | null;
  onPick: (file: File | null) => void;
  onClearPending: () => void;
  disabled?: boolean;
  onOpenAttachment?: (ref: CitAttachmentRef) => void;
  compact?: boolean;
  hideLink?: boolean;
}) {
  return (
    <div className={`cit-form__upload${compact ? ' cit-form__upload--compact' : ''}`}>
      <div className="cit-form__upload-controls">
        <label className={`btn-secondary btn-sm cit-form__upload-btn${disabled ? ' is-disabled' : ''}`}>
          {pending || saved ? 'Replace' : 'Upload'}
          <input
            type="file"
            hidden
            disabled={disabled}
            accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx,.xls,.xlsx,application/pdf,image/*"
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              onPick(e.target.files?.[0] ?? null);
              e.target.value = '';
            }}
          />
        </label>
        {pending && !disabled ? (
          <button type="button" className="btn-secondary btn-sm" onClick={onClearPending}>
            Clear
          </button>
        ) : null}
      </div>
      {hideLink ? null : (
        <FileNameLine saved={saved} pending={pending} onOpenAttachment={onOpenAttachment} />
      )}
    </div>
  );
}

export function CustomiseItemsTransferForm({
  value,
  onChange,
  pendingFiles,
  onPendingFilesChange,
  disabled,
  sectionLocks = { general: false, deposit: false, offset: false, clearing: false },
  committedIds = EMPTY_CIT_COMMITTED_IDS,
  onRequestEditSection,
  onCancelEditSection,
  onOpenAttachment,
  onCreateStockTransferForm,
  creatingStockTransferOffsetId = null,
}: Props) {
  const [generalOpen, setGeneralOpen] = useState(true);
  const [depositExtractBusy, setDepositExtractBusy] = useState<Record<string, boolean>>({});
  const [depositExtractError, setDepositExtractError] = useState<Record<string, string | null>>({});
  const valueRef = useRef(value);
  valueRef.current = value;
  const generalMetaDisabled = Boolean(disabled || sectionLocks.general);
  /** PowerBI SOC import — NAV fields stay read-only only until SC file confirms (stops daily upsert). */
  const fromPowerBi = value.import_source === 'powerbi';
  const scConfirmed = citHasSalesConfirmationAttachment(
    value,
    pendingFiles.sales_confirmation,
  );
  const pbiSyncLocked = fromPowerBi && !scConfirmed;

  function patch(partial: Partial<CustomiseItemsTransferFormData>) {
    onChange({ ...valueRef.current, ...partial });
  }

  function updateItem(id: string, partial: Partial<CitItemRow>) {
    const current = valueRef.current;
    onChange({
      ...current,
      items: current.items.map((row) => (row.id === id ? { ...row, ...partial } : row)),
    });
  }

  function updateDoc(id: string, partial: Partial<CitDocumentRow>) {
    const current = valueRef.current;
    onChange({
      ...current,
      deposit_invoices: current.deposit_invoices.map((row) =>
        row.id === id ? { ...row, ...partial } : row,
      ),
    });
  }

  async function handlePickDepositFile(id: string, file: File | null) {
    onPendingFilesChange({
      ...pendingFiles,
      deposit: { ...pendingFiles.deposit, [id]: file },
    });
    setDepositExtractError((prev) => ({ ...prev, [id]: null }));
    if (!file) {
      setDepositExtractBusy((prev) => ({ ...prev, [id]: false }));
      return;
    }

    setDepositExtractBusy((prev) => ({ ...prev, [id]: true }));
    try {
      const extracted = await extractCitDepositInvoiceFromFile(file);
      const current = valueRef.current;
      const generalItemNos = current.items.map((row) => row.item_no.trim()).filter(Boolean);
      const lines = mapDepositExtractToCitLines(extracted.lines, generalItemNos);
      const patchRow: Partial<CitDocumentRow> = { lines };
      if (extracted.invoice_no) patchRow.number = extracted.invoice_no;
      if (extracted.invoice_amount) patchRow.invoice_amount = extracted.invoice_amount;
      updateDoc(id, patchRow);
      if (!extracted.invoice_no && !extracted.invoice_amount && extracted.lines.length === 0) {
        setDepositExtractError((prev) => ({
          ...prev,
          [id]: 'Could not read invoice fields from this PDF. Fill them manually.',
        }));
      }
    } catch (err) {
      setDepositExtractError((prev) => ({
        ...prev,
        [id]: err instanceof Error ? err.message : 'Deposit AI extract failed.',
      }));
    } finally {
      setDepositExtractBusy((prev) => ({ ...prev, [id]: false }));
    }
  }

  function updateOffset(id: string, partial: Partial<CitOffsetRow>) {
    patch({
      offset_invoices: value.offset_invoices.map((row) =>
        row.id === id ? { ...row, ...partial } : row,
      ),
    });
  }

  const itemOptions = value.items
    .map((row) => row.item_no.trim())
    .filter(Boolean)
    .filter((code, index, arr) => arr.indexOf(code) === index);

  const itemUomByCode = useMemo(() => {
    const map: Record<string, string> = {};
    for (const row of value.items) {
      const code = row.item_no.trim().toUpperCase();
      const uom = row.uom.trim();
      if (code && uom && !map[code]) map[code] = uom;
    }
    return map;
  }, [value.items]);

  const offsetOverPaid = citOffsetOverPaidItems(value);

  useEffect(() => {
    if (isCitCreditCancelled(value)) {
      if (value.status === 'Cancelled') return;
      onChange({ ...value, status: 'Cancelled' });
      return;
    }
    if (value.status === 'Cancelled') return;
    const next = deriveCitAutoStatus(value);
    if (next === value.status) return;
    onChange({ ...value, status: next });
  }, [value.items, value.offset_invoices, value.deposit_invoices, value.status, value, onChange]);

  function itemRowDisabled(id: string) {
    return Boolean(disabled || (sectionLocks.general && isCommitted(id, committedIds.items)));
  }

  function canRemoveItem(id: string) {
    if (disabled) return false;
    if (pbiSyncLocked) return false;
    if (value.items.length <= 1) return false;
    if (sectionLocks.general && isCommitted(id, committedIds.items)) return false;
    return true;
  }

  const req = CIT_ENFORCE_MANDATORY ? ' *' : '';

  return (
    <div className={`cit-form${generalMetaDisabled ? ' is-readonly' : ''}`}>
      {!CIT_ENFORCE_MANDATORY ? (
        <p className="field-hint cit-form__intro">
          Import mode: supporting files and most required fields are optional for now. Only Sales
          confirmation no. is required to save.
        </p>
      ) : null}
      <section
        className={`cit-form__panel${generalOpen ? '' : ' is-collapsed'}${
          sectionLocks.general ? ' is-section-locked' : ''
        }`}
      >
        <div className="cit-form__panel-head">
          <button
            type="button"
            className="cit-form__panel-toggle"
            aria-expanded={generalOpen}
            onClick={() => setGeneralOpen((open) => !open)}
          >
            <span className="cit-form__chevron" aria-hidden>
              {generalOpen ? '▾' : '▸'}
            </span>
            <span className="cit-form__panel-title">General</span>
            {sectionLocks.general ? <span className="cit-form__lock-pill">Locked</span> : null}
          </button>
          {sectionLocks.general && onRequestEditSection ? (
            <button
              type="button"
              className="cit-form__edit-link"
              onClick={() => onRequestEditSection('general')}
            >
              Edit
            </button>
          ) : null}
          {!sectionLocks.general && onCancelEditSection ? (
            <button
              type="button"
              className="cit-form__edit-link"
              onClick={() => onCancelEditSection('general')}
            >
              Cancel edit
            </button>
          ) : null}
        </div>

        {generalOpen ? (
          <div className="cit-form__panel-body">
            {sectionLocks.general ? (
              <p className="field-hint cit-form__lock-hint">
                Existing general fields are locked. You can still add new item rows, or select Edit to
                change or remove saved details.
              </p>
            ) : null}

            <div className="cit-form__grid3">
              <div className="field cit-form__sc-field">
                <span className="field-label">Sales confirmation no.{req}</span>
                <div className="cit-form__sc-combo">
                  {generalMetaDisabled &&
                  value.sales_confirmation_file &&
                  onOpenAttachment &&
                  value.sales_confirmation_no.trim() ? (
                    <button
                      type="button"
                      className={`${inputClass(true, 'mono')} cit-form__value-link`}
                      onClick={() => onOpenAttachment(value.sales_confirmation_file!)}
                      title={value.sales_confirmation_file.fileName}
                    >
                      {value.sales_confirmation_no}
                    </button>
                  ) : (
                    <input
                      className={inputClass(
                        generalMetaDisabled || pbiSyncLocked,
                        'mono',
                      )}
                      value={value.sales_confirmation_no}
                      onChange={(e) => patch({ sales_confirmation_no: e.target.value })}
                      disabled={generalMetaDisabled || pbiSyncLocked}
                      readOnly={pbiSyncLocked}
                      placeholder="Enter sales confirmation number"
                      aria-label="Sales confirmation number"
                      title={
                        pbiSyncLocked
                          ? 'Imported from PowerBI — upload SC file to stop daily sync, then Edit to change'
                          : undefined
                      }
                    />
                  )}
                  {!generalMetaDisabled ? (
                    <FilePicker
                      saved={value.sales_confirmation_file}
                      pending={pendingFiles.sales_confirmation}
                      onPick={(file) =>
                        onPendingFilesChange({ ...pendingFiles, sales_confirmation: file })
                      }
                      onClearPending={() =>
                        onPendingFilesChange({ ...pendingFiles, sales_confirmation: null })
                      }
                      hideLink
                    />
                  ) : null}
                </div>
                {!generalMetaDisabled && pendingFiles.sales_confirmation ? (
                  <p className="field-hint cit-form__file-name">
                    Selected: {pendingFiles.sales_confirmation.name}
                  </p>
                ) : null}
                {!generalMetaDisabled &&
                !pendingFiles.sales_confirmation &&
                value.sales_confirmation_file &&
                onOpenAttachment &&
                value.sales_confirmation_no.trim() ? (
                  <p className="field-hint cit-form__file-name">
                    <button
                      type="button"
                      className="cit-form__file-link"
                      onClick={() => onOpenAttachment(value.sales_confirmation_file!)}
                      title={value.sales_confirmation_file.fileName}
                    >
                      {value.sales_confirmation_no.trim()}
                    </button>
                  </p>
                ) : null}
              </div>

              <label className="field cit-form__needs-deposit">
                <span className="field-label">Deposit arrangement?{req}</span>
                <select
                  className={inputClass(generalMetaDisabled || !scConfirmed)}
                  value={
                    value.needs_deposit === true
                      ? 'yes'
                      : value.needs_deposit === false
                        ? 'no'
                        : ''
                  }
                  disabled={generalMetaDisabled || !scConfirmed}
                  title={
                    scConfirmed
                      ? undefined
                      : fromPowerBi
                        ? 'Upload the sales confirmation file to choose whether deposit / offset is needed. Until then, PowerBI will refresh customer / items each day.'
                        : 'Upload the sales confirmation file to choose whether deposit / offset is needed.'
                  }
                  onChange={(e) => {
                    const next = e.target.value;
                    if (next === 'yes') {
                      patch({
                        needs_deposit: true,
                        deposit_invoices:
                          value.deposit_invoices.length > 0
                            ? value.deposit_invoices
                            : [createCitDocumentRow()],
                      });
                      return;
                    }
                    if (next === 'no') {
                      patch({ needs_deposit: false });
                      return;
                    }
                    patch({ needs_deposit: null });
                  }}
                >
                  <option value="">
                    {scConfirmed ? 'Select…' : 'Upload SC first'}
                  </option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </label>

              <label className="field cit-form__status-field">
                <span className="field-label">Status{req}</span>
                <select
                  className={inputClass(generalMetaDisabled)}
                  value={value.status}
                  onChange={(e) => patch({ status: e.target.value as CitTransferStatus })}
                  disabled={generalMetaDisabled}
                >
                  {CIT_STATUS_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className="cit-form__customer-row">
              <label className="field cit-form__code-field">
                <span className="field-label">Customer no.{req}</span>
                <CffAsyncTypeahead
                  value={value.customer_code}
                  onChange={(text) => {
                    if (!text.trim()) {
                      patch({ customer_code: '', customer_name: '' });
                      return;
                    }
                    patch({ customer_code: text, customer_name: value.customer_name });
                  }}
                  onSelect={(opt) => {
                    const customer = customerFromMeta(opt.meta);
                    patch({
                      customer_code: customer?.code ?? opt.value,
                      customer_name: customer?.name ?? '',
                    });
                  }}
                  search={searchCustomers}
                  placeholder="Search by code or name"
                  disabled={generalMetaDisabled || pbiSyncLocked}
                  inputClassName={inputClass(generalMetaDisabled || pbiSyncLocked, 'mono')}
                  ariaLabel="Customer number"
                />
              </label>
              <label className="field cit-form__name-field">
                <span className="field-label">Customer name{req}</span>
                <input
                  className={inputClass(generalMetaDisabled || pbiSyncLocked)}
                  value={value.customer_name}
                  onChange={(e) => patch({ customer_name: e.target.value })}
                  disabled={generalMetaDisabled || pbiSyncLocked}
                  readOnly={pbiSyncLocked}
                  placeholder="Filled from customer lookup"
                  aria-label="Customer name"
                />
              </label>
            </div>

            <div className="cit-form__table-toolbar">
              <span className="cit-form__table-title">Items{req}</span>
              {!disabled && !pbiSyncLocked ? (
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  onClick={() => patch({ items: [...value.items, createCitItemRow()] })}
                >
                  + Add item
                </button>
              ) : null}
            </div>

            <div className="cit-form__table-wrap">
              <table className="cit-form__table">
                <thead>
                  <tr>
                    <th className="cit-form__col-item-no">Item no.</th>
                    <th className="cit-form__col-item-transfer">Item no. (Transfer)</th>
                    <th>Description</th>
                    <th>UOM</th>
                    <th>SOC qty</th>
                    <th className="cit-form__col-paid">Paid qty</th>
                    <th className="cit-form__col-offset">Offset qty</th>
                    <th className="cit-form__col-balance">Balance qty</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {value.items.map((row) => {
                    const rowDisabled = itemRowDisabled(row.id);
                    const navFieldsLocked = rowDisabled || pbiSyncLocked;
                    const paidQty = sumPaidQtyForItem(value, row.item_no);
                    const offsetQty = sumOffsetQtyForItem(value, row.item_no);
                    const balanceQty = paidQty - offsetQty;
                    const offsetOverPaidQty = offsetQty > paidQty + 1e-9;
                    return (
                      <tr
                        key={row.id}
                        className={rowDisabled || pbiSyncLocked ? 'is-locked' : undefined}
                      >
                        <td className="cit-form__col-item-no">
                          <CffAsyncTypeahead
                            value={row.item_no}
                            onChange={(item_no) => {
                              if (!item_no.trim()) {
                                updateItem(row.id, {
                                  item_no: '',
                                  description: '',
                                  uom: '',
                                });
                                return;
                              }
                              updateItem(row.id, { item_no });
                            }}
                            onSelect={(opt) => {
                              const item = itemFromMeta(opt.meta);
                              updateItem(row.id, {
                                item_no: item?.code ?? opt.value,
                                description: item?.description ?? '',
                                uom: item?.uom ?? '',
                              });
                            }}
                            search={searchItems}
                            placeholder="Search item"
                            disabled={navFieldsLocked}
                            inputClassName={inputClass(navFieldsLocked, 'mono')}
                            ariaLabel="Item number"
                          />
                        </td>
                        <td className="cit-form__col-item-transfer">
                          <input
                            className={inputClass(rowDisabled, 'mono')}
                            value={row.item_no_transfer}
                            onChange={(e) =>
                              updateItem(row.id, { item_no_transfer: e.target.value })
                            }
                            disabled={rowDisabled}
                            placeholder="Transfer-to item"
                          />
                        </td>
                        <td>
                          <ReadonlyValue
                            value={row.description}
                            placeholder="Auto from item"
                          />
                        </td>
                        <td>
                          <input
                            className={inputClass(navFieldsLocked)}
                            value={row.uom}
                            onChange={(e) => updateItem(row.id, { uom: e.target.value })}
                            disabled={navFieldsLocked}
                            placeholder="UOM"
                            title={pbiSyncLocked ? 'From PowerBI until SC file is uploaded' : undefined}
                          />
                        </td>
                        <td>
                          <input
                            className={inputClass(navFieldsLocked)}
                            type="number"
                            min="0"
                            step="any"
                            value={row.sales_confirmation_qty}
                            onChange={(e) =>
                              updateItem(row.id, { sales_confirmation_qty: e.target.value })
                            }
                            disabled={navFieldsLocked}
                            placeholder="Qty"
                            aria-label="SOC qty"
                            title={pbiSyncLocked ? 'From PowerBI until SC file is uploaded' : undefined}
                          />
                        </td>
                        <td className="cit-form__col-paid">
                          <ReadonlyValue
                            value={formatCitQty(paidQty)}
                            className="cit-form__readonly-value--qty"
                          />
                        </td>
                        <td className="cit-form__col-offset">
                          <ReadonlyValue
                            value={formatCitQty(offsetQty)}
                            className={`cit-form__readonly-value--qty${
                              offsetOverPaidQty ? ' cit-form__readonly-value--qty-neg' : ''
                            }`}
                          />
                        </td>
                        <td className="cit-form__col-balance">
                          <ReadonlyValue
                            value={formatCitQty(balanceQty)}
                            className={`cit-form__readonly-value--qty${
                              balanceQty < 0 ? ' cit-form__readonly-value--qty-neg' : ''
                            }`}
                          />
                        </td>
                        <td className="cit-form__table-actions">
                          {canRemoveItem(row.id) ? (
                            <button
                              type="button"
                              className="btn-secondary btn-sm"
                              aria-label="Remove item"
                              onClick={() =>
                                patch({ items: value.items.filter((item) => item.id !== row.id) })
                              }
                            >
                              ×
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : null}
      </section>

      {citShowDepositSections(value, pendingFiles.sales_confirmation) ? (
      <DepositPairSection
        rows={value.deposit_invoices}
        itemOptions={itemOptions}
        itemUomByCode={itemUomByCode}
        pendingDeposit={pendingFiles.deposit}
        pendingPayment={pendingFiles.payment}
        disabled={disabled}
        sectionLocked={sectionLocks.deposit}
        committedIds={committedIds.deposit}
        onRequestEdit={
          onRequestEditSection ? () => onRequestEditSection('deposit') : undefined
        }
        onCancelEdit={
          onCancelEditSection ? () => onCancelEditSection('deposit') : undefined
        }
        onAdd={() => {
          onRequestEditSection?.('deposit');
          patch({ deposit_invoices: [...value.deposit_invoices, createCitDocumentRow()] });
        }}
        onChangeRow={(id, partial) => updateDoc(id, partial)}
        onRemove={(id) => {
          const target = value.deposit_invoices.find((row) => row.id === id);
          patch({
            deposit_invoices:
              value.deposit_invoices.length <= 1
                ? [createCitDocumentRow()]
                : value.deposit_invoices.filter((row) => row.id !== id),
          });
          const nextDeposit = { ...pendingFiles.deposit };
          const nextPayment = { ...pendingFiles.payment };
          delete nextDeposit[id];
          for (const receipt of target?.payment_receipts ?? []) {
            delete nextPayment[receipt.id];
          }
          onPendingFilesChange({
            ...pendingFiles,
            deposit: nextDeposit,
            payment: nextPayment,
          });
        }}
        depositExtractBusy={depositExtractBusy}
        depositExtractError={depositExtractError}
        onPickDepositFile={(id, file) => void handlePickDepositFile(id, file)}
        onClearDepositFile={(id) => {
          setDepositExtractError((prev) => ({ ...prev, [id]: null }));
          setDepositExtractBusy((prev) => ({ ...prev, [id]: false }));
          onPendingFilesChange({
            ...pendingFiles,
            deposit: { ...pendingFiles.deposit, [id]: null },
          });
        }}
        onPickPaymentFile={(id, file) =>
          onPendingFilesChange({
            ...pendingFiles,
            payment: { ...pendingFiles.payment, [id]: file },
          })
        }
        onClearPaymentFile={(id) =>
          onPendingFilesChange({
            ...pendingFiles,
            payment: { ...pendingFiles.payment, [id]: null },
          })
        }
        onOpenAttachment={onOpenAttachment}
      />
      ) : null}

      {citShowClearingInvoices(value, pendingFiles.sales_confirmation) ? (
        value.clearing_invoices.length === 0 ? (
          <section className="cit-form__panel cit-form__panel--clearing cit-form__panel--clearing-idle">
            <div className="cit-form__panel-head">
              <div className="cit-form__panel-heading">
                <span className="cit-form__panel-heading-text">
                  <span className="cit-form__panel-title">Tooling invoices</span>
                  <span className="cit-form__panel-subtitle">
                    Optional — mock-up / block charge ($0), invoice no. only, no offset
                  </span>
                </span>
              </div>
              {!disabled ? (
                <div className="cit-form__panel-actions">
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    onClick={() => {
                      onRequestEditSection?.('clearing');
                      patch({ clearing_invoices: [createCitClearingInvoiceRow()] });
                    }}
                  >
                    + Add tooling invoice
                  </button>
                </div>
              ) : null}
            </div>
          </section>
        ) : (
          <ClearingInvoiceSection
            rows={value.clearing_invoices}
            pendingMap={pendingFiles.clearing ?? {}}
            disabled={disabled}
            sectionLocked={sectionLocks.clearing}
            committedIds={committedIds.clearing}
            onRequestEdit={
              onRequestEditSection ? () => onRequestEditSection('clearing') : undefined
            }
            onCancelEdit={
              onCancelEditSection ? () => onCancelEditSection('clearing') : undefined
            }
            onAdd={() => {
              onRequestEditSection?.('clearing');
              patch({
                clearing_invoices: [
                  ...value.clearing_invoices,
                  createCitClearingInvoiceRow(),
                ],
              });
            }}
            onChangeRow={(id, partial) =>
              patch({
                clearing_invoices: value.clearing_invoices.map((row) =>
                  row.id === id ? { ...row, ...partial } : row,
                ),
              })
            }
            onRemove={(id) => {
              patch({
                clearing_invoices: value.clearing_invoices.filter((row) => row.id !== id),
              });
              const nextClearing = { ...pendingFiles.clearing };
              delete nextClearing[id];
              onPendingFilesChange({ ...pendingFiles, clearing: nextClearing });
            }}
            onPickFile={(id, file) =>
              onPendingFilesChange({
                ...pendingFiles,
                clearing: { ...pendingFiles.clearing, [id]: file },
              })
            }
            onClearFile={(id) =>
              onPendingFilesChange({
                ...pendingFiles,
                clearing: { ...pendingFiles.clearing, [id]: null },
              })
            }
            onOpenAttachment={onOpenAttachment}
          />
        )
      ) : null}

      {citShowDepositSections(value, pendingFiles.sales_confirmation) ? (
        value.offset_invoices.length === 0 ? (
          <section className="cit-form__panel cit-form__panel--offset cit-form__panel--offset-idle">
            <div className="cit-form__panel-head">
              <div className="cit-form__panel-heading">
                <span className="cit-form__step-chip" aria-hidden>
                  2
                </span>
                <span className="cit-form__panel-heading-text">
                  <span className="cit-form__panel-title">Offset invoices</span>
                  <span className="cit-form__panel-subtitle">
                    Optional — start when you are ready to offset against deposit
                  </span>
                </span>
              </div>
              {!disabled ? (
                <div className="cit-form__panel-actions">
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    onClick={() => {
                      onRequestEditSection?.('offset');
                      patch({ offset_invoices: [createCitOffsetRow()] });
                    }}
                  >
                    + Start offset
                  </button>
                </div>
              ) : null}
            </div>
          </section>
        ) : (
          <OffsetPairSection
            rows={value.offset_invoices}
            itemOptions={itemOptions}
            itemUomByCode={itemUomByCode}
            offsetOverPaid={offsetOverPaid}
            pendingMap={pendingFiles.offset}
            disabled={disabled}
            sectionLocked={sectionLocks.offset}
            committedIds={committedIds.offset}
            onRequestEdit={
              onRequestEditSection ? () => onRequestEditSection('offset') : undefined
            }
            onCancelEdit={
              onCancelEditSection ? () => onCancelEditSection('offset') : undefined
            }
            onAdd={() => {
              onRequestEditSection?.('offset');
              patch({ offset_invoices: [...value.offset_invoices, createCitOffsetRow()] });
            }}
            onChangeRow={updateOffset}
            onRemove={(id) => {
              const remaining = value.offset_invoices.filter((row) => row.id !== id);
              patch({ offset_invoices: remaining });
              const nextOffset = { ...pendingFiles.offset };
              delete nextOffset[id];
              onPendingFilesChange({ ...pendingFiles, offset: nextOffset });
            }}
            onPickFile={(id, file) =>
              onPendingFilesChange({
                ...pendingFiles,
                offset: { ...pendingFiles.offset, [id]: file },
              })
            }
            onClearFile={(id) =>
              onPendingFilesChange({
                ...pendingFiles,
                offset: { ...pendingFiles.offset, [id]: null },
              })
            }
            onOpenAttachment={onOpenAttachment}
            onCreateStockTransferForm={onCreateStockTransferForm}
            creatingStockTransferOffsetId={creatingStockTransferOffsetId}
          />
        )
      ) : null}
    </div>
  );
}

function ClearingInvoiceSection({
  rows,
  pendingMap,
  disabled,
  sectionLocked,
  committedIds,
  onRequestEdit,
  onCancelEdit,
  onAdd,
  onChangeRow,
  onRemove,
  onPickFile,
  onClearFile,
  onOpenAttachment,
}: {
  rows: CitClearingInvoiceRow[];
  pendingMap: Record<string, File | null>;
  disabled?: boolean;
  sectionLocked: boolean;
  committedIds: string[];
  onRequestEdit?: () => void;
  onCancelEdit?: () => void;
  onAdd: () => void;
  onChangeRow: (id: string, partial: Partial<CitClearingInvoiceRow>) => void;
  onRemove: (id: string) => void;
  onPickFile: (id: string, file: File | null) => void;
  onClearFile: (id: string) => void;
  onOpenAttachment?: (ref: CitAttachmentRef) => void;
}) {
  const [open, setOpen] = useState(true);

  return (
    <section
      className={`cit-form__panel cit-form__panel--clearing${open ? '' : ' is-collapsed'}${
        sectionLocked ? ' is-section-locked' : ''
      }`}
    >
      <div className="cit-form__panel-head">
        <button
          type="button"
          className="cit-form__panel-toggle"
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
        >
          <span className="cit-form__chevron" aria-hidden>
            {open ? '▾' : '▸'}
          </span>
          <span className="cit-form__panel-heading">
            <span className="cit-form__panel-heading-text">
              <span className="cit-form__panel-title">Tooling invoices</span>
              <span className="cit-form__panel-subtitle">
                Optional — mock-up / block charge ($0), invoice no. only, no offset
              </span>
            </span>
          </span>
          {sectionLocked ? <span className="cit-form__lock-pill">Locked</span> : null}
        </button>
        <div className="cit-form__panel-actions">
          {sectionLocked && onRequestEdit ? (
            <button
              type="button"
              className="cit-form__edit-link"
              onClick={() => {
                setOpen(true);
                onRequestEdit();
              }}
            >
              Edit
            </button>
          ) : null}
          {!sectionLocked && onCancelEdit ? (
            <button type="button" className="cit-form__edit-link" onClick={onCancelEdit}>
              Cancel edit
            </button>
          ) : null}
          {!disabled ? (
            <button
              type="button"
              className="btn-secondary btn-sm"
              onClick={() => {
                setOpen(true);
                onAdd();
              }}
            >
              + Add tooling invoice
            </button>
          ) : null}
        </div>
      </div>

      {open ? (
        <div className="cit-form__panel-body">
          <div className="cit-form__pair-list">
            {rows.map((row, index) => {
              const rowLocked = sectionLocked && isCommitted(row.id, committedIds);
              const fieldsDisabled = Boolean(disabled || rowLocked);
              const canRemove =
                !disabled &&
                rows.length > 0 &&
                (!sectionLocked || !isCommitted(row.id, committedIds));
              return (
                <div
                  key={row.id}
                  className={`cit-form__pair-row cit-form__pair-row--clearing${
                    rowLocked ? ' is-locked' : ''
                  }`}
                >
                  <div className="cit-form__pair-half-head">
                    <div className="cit-form__pair-half-title">
                      Tooling invoice {rows.length > 1 ? index + 1 : ''}
                    </div>
                    {canRemove && !fieldsDisabled ? (
                      <button
                        type="button"
                        className="btn-secondary btn-sm"
                        aria-label="Remove tooling invoice"
                        onClick={() => onRemove(row.id)}
                      >
                        Remove
                      </button>
                    ) : null}
                  </div>
                  <div className="field cit-form__kv">
                    <span className="field-label">
                      Tooling invoice no.{rows.length > 1 ? ` ${index + 1}` : ''}
                    </span>
                    <LinkedDocNumber
                      value={row.number}
                      onChange={(number) => onChangeRow(row.id, { number })}
                      disabled={fieldsDisabled}
                      placeholder="e.g. INV / SCN number"
                      saved={row.file}
                      pending={pendingMap[row.id] ?? null}
                      onPick={(file) => onPickFile(row.id, file)}
                      onClearPending={() => onClearFile(row.id)}
                      uploadDisabled={fieldsDisabled}
                      onOpenAttachment={onOpenAttachment}
                      ariaLabel={`Tooling invoice ${index + 1} number`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
    </section>
  );
}

function DepositPairSection({
  rows,
  itemOptions,
  itemUomByCode,
  pendingDeposit,
  pendingPayment,
  depositExtractBusy,
  depositExtractError,
  disabled,
  sectionLocked,
  committedIds,
  onRequestEdit,
  onCancelEdit,
  onAdd,
  onChangeRow,
  onRemove,
  onPickDepositFile,
  onClearDepositFile,
  onPickPaymentFile,
  onClearPaymentFile,
  onOpenAttachment,
}: {
  rows: CitDocumentRow[];
  itemOptions: string[];
  itemUomByCode: Record<string, string>;
  pendingDeposit: Record<string, File | null>;
  pendingPayment: Record<string, File | null>;
  depositExtractBusy: Record<string, boolean>;
  depositExtractError: Record<string, string | null>;
  disabled?: boolean;
  sectionLocked: boolean;
  committedIds: string[];
  onRequestEdit?: () => void;
  onCancelEdit?: () => void;
  onAdd: () => void;
  onChangeRow: (id: string, partial: Partial<CitDocumentRow>) => void;
  onRemove: (id: string) => void;
  onPickDepositFile: (id: string, file: File | null) => void;
  onClearDepositFile: (id: string) => void;
  onPickPaymentFile: (id: string, file: File | null) => void;
  onClearPaymentFile: (id: string) => void;
  onOpenAttachment?: (ref: CitAttachmentRef) => void;
}) {
  const [open, setOpen] = useState(true);
  const req = CIT_ENFORCE_MANDATORY ? ' *' : '';
  const fileReq = CIT_REQUIRE_SUPPORTING_FILES ? ' *' : '';
  const paymentFileReq = CIT_REQUIRE_PAYMENT_SUPPORTING_FILE ? ' *' : '';

  function updateLine(row: CitDocumentRow, lineId: string, partial: Partial<CitDepositLine>) {
    onChangeRow(row.id, {
      lines: row.lines.map((line) => (line.id === lineId ? { ...line, ...partial } : line)),
    });
  }

  function addLine(row: CitDocumentRow) {
    onChangeRow(row.id, { lines: [...row.lines, createCitDepositLine()] });
  }

  function removeLine(row: CitDocumentRow, lineId: string) {
    if (row.lines.length <= 1) return;
    onChangeRow(row.id, { lines: row.lines.filter((line) => line.id !== lineId) });
  }

  return (
    <section
      className={`cit-form__panel cit-form__panel--deposit${open ? '' : ' is-collapsed'}${
        sectionLocked ? ' is-section-locked' : ''
      }`}
    >
      <div className="cit-form__panel-head">
        <button
          type="button"
          className="cit-form__panel-toggle"
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
        >
          <span className="cit-form__chevron" aria-hidden>
            {open ? '▾' : '▸'}
          </span>
          <span className="cit-form__panel-heading">
            <span className="cit-form__step-chip" aria-hidden>
              1
            </span>
            <span className="cit-form__panel-heading-text">
              <span className="cit-form__panel-title">Deposit invoices{req}</span>
              <span className="cit-form__panel-subtitle">
                Customer deposit invoice + payment receipt / credit memo
              </span>
            </span>
          </span>
          {sectionLocked ? <span className="cit-form__lock-pill">Locked</span> : null}
        </button>
        <div className="cit-form__panel-actions">
          {sectionLocked && onRequestEdit ? (
            <button
              type="button"
              className="cit-form__edit-link"
              onClick={() => {
                setOpen(true);
                onRequestEdit();
              }}
            >
              Edit
            </button>
          ) : null}
          {!sectionLocked && onCancelEdit ? (
            <button
              type="button"
              className="cit-form__edit-link"
              onClick={() => {
                setOpen(true);
                onCancelEdit();
              }}
            >
              Cancel edit
            </button>
          ) : null}
          {!disabled ? (
            <button
              type="button"
              className="btn-secondary btn-sm"
              onClick={() => {
                setOpen(true);
                onAdd();
              }}
            >
              + Add pair
            </button>
          ) : null}
        </div>
      </div>
      {open ? (
        <div className="cit-form__panel-body">
          {sectionLocked ? (
            <p className="field-hint cit-form__lock-hint">
              Saved pairs are locked. Add a new pair below, or select Edit to change or remove
              existing ones.
            </p>
          ) : null}
          {itemOptions.length === 0 ? (
            <p className="field-hint">Add item numbers in General first to link deposit invoices.</p>
          ) : null}
          <div className="cit-form__pair-list">
            {rows.map((row, index) => {
              const rowLocked = sectionLocked && isCommitted(row.id, committedIds);
              const rowDisabled = Boolean(disabled || rowLocked);
              const canRemove =
                !disabled &&
                rows.length > 1 &&
                (!sectionLocked || !isCommitted(row.id, committedIds));
              return (
                <div
                  key={row.id}
                  className={`cit-form__pair-row cit-form__pair-row--offset cit-form__pair-row--deposit${
                    rowLocked ? ' is-locked' : ''
                  }`}
                >
                  <div className="cit-form__offset-top">
                    <div className="cit-form__lane cit-form__lane--primary">
                      <div className="cit-form__pair-half-head">
                        <div className="cit-form__pair-half-title">
                          Deposit invoice {rows.length > 1 ? index + 1 : ''}
                        </div>
                        {canRemove ? (
                          <button
                            type="button"
                            className="btn-secondary btn-sm"
                            aria-label="Remove pair"
                            onClick={() => onRemove(row.id)}
                          >
                            Remove pair
                          </button>
                        ) : null}
                      </div>
                      <div className="cit-form__kv-stack">
                        <div className="field cit-form__kv">
                          <span className="field-label">
                            Deposit invoice no.{req}
                            {fileReq}
                          </span>
                          <LinkedDocNumber
                            value={row.number}
                            onChange={(number) => onChangeRow(row.id, { number })}
                            disabled={rowDisabled}
                            placeholder="Invoice number"
                            saved={row.file}
                            pending={pendingDeposit[row.id] ?? null}
                            onPick={(file) => onPickDepositFile(row.id, file)}
                            onClearPending={() => onClearDepositFile(row.id)}
                            uploadDisabled={
                              rowDisabled || Boolean(depositExtractBusy[row.id])
                            }
                            onOpenAttachment={onOpenAttachment}
                            ariaLabel="Deposit invoice number"
                          />
                          {depositExtractBusy[row.id] ? (
                            <p className="field-hint">Reading invoice with AI…</p>
                          ) : null}
                          {depositExtractError[row.id] ? (
                            <p className="field-hint cit-form__extract-error" role="status">
                              {depositExtractError[row.id]}
                            </p>
                          ) : null}
                          {!rowDisabled &&
                          !depositExtractBusy[row.id] &&
                          !depositExtractError[row.id] ? (
                            <p className="field-hint">
                              PDF upload can auto-fill invoice no., amount (including GST),
                              item and qty.
                            </p>
                          ) : null}
                        </div>
                        <label className="field cit-form__kv">
                          <span className="field-label">Invoice amount{req}</span>
                          <AmountField
                            value={row.invoice_amount}
                            onChange={(invoice_amount) =>
                              onChangeRow(row.id, { invoice_amount })
                            }
                            disabled={rowDisabled}
                          />
                        </label>
                      </div>
                    </div>
                    <div className="cit-form__lane cit-form__lane--secondary">
                      <div className="cit-form__pair-half-head">
                        <div className="cit-form__pair-half-title">
                          Payment receipt / Sales credit memo
                        </div>
                        {!rowDisabled ? (
                          <button
                            type="button"
                            className="btn-secondary btn-sm cit-form__receipt-add"
                            onClick={() =>
                              onChangeRow(row.id, {
                                payment_receipts: [
                                  ...row.payment_receipts,
                                  createCitLinkedReceipt(),
                                ],
                              })
                            }
                          >
                            + Add receipt
                          </button>
                        ) : null}
                      </div>
                      <div className="cit-form__receipt-list">
                        {row.payment_receipts.map((receipt, receiptIndex) => (
                          <div key={receipt.id} className="cit-form__receipt-item">
                            <div className="cit-form__receipt-item-head">
                              <span className="field-label">
                                No.
                                {row.payment_receipts.length > 1 ? ` ${receiptIndex + 1}` : ''}
                                {paymentFileReq}
                              </span>
                              {!rowDisabled && row.payment_receipts.length > 1 ? (
                                <button
                                  type="button"
                                  className="btn-secondary btn-sm"
                                  aria-label="Remove receipt"
                                  onClick={() => {
                                    const nextReceipts = row.payment_receipts.filter(
                                      (item) => item.id !== receipt.id,
                                    );
                                    onChangeRow(row.id, {
                                      payment_receipts:
                                        nextReceipts.length > 0
                                          ? nextReceipts
                                          : [createCitLinkedReceipt()],
                                    });
                                    onClearPaymentFile(receipt.id);
                                  }}
                                >
                                  Remove
                                </button>
                              ) : null}
                            </div>
                            <LinkedDocNumber
                              value={receipt.number}
                              onChange={(number) => {
                                const nextReceipts = row.payment_receipts.map((item) =>
                                  item.id === receipt.id ? { ...item, number } : item,
                                );
                                onChangeRow(row.id, { payment_receipts: nextReceipts });
                              }}
                              disabled={rowDisabled}
                              placeholder="Receipt / SCM number"
                              saved={receipt.file}
                              pending={pendingPayment[receipt.id] ?? null}
                              onPick={(file) => onPickPaymentFile(receipt.id, file)}
                              onClearPending={() => onClearPaymentFile(receipt.id)}
                              uploadDisabled={rowDisabled}
                              onOpenAttachment={onOpenAttachment}
                              ariaLabel="Payment receipt / sales credit memo number"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="cit-form__offset-table-wrap">
                    <div className="cit-form__offset-lines-head">
                      <span className="field-label">Items &amp; quantity{req}</span>
                      {!rowDisabled ? (
                        <button
                          type="button"
                          className="btn-secondary btn-sm"
                          onClick={() => addLine(row)}
                        >
                          + Add item
                        </button>
                      ) : null}
                    </div>
                    <div className="cit-form__offset-table-scroll">
                      <table className="cit-form__offset-table cit-form__offset-table--deposit">
                        <thead>
                          <tr>
                            <th scope="col">Item</th>
                            <th scope="col">Qty</th>
                            <th scope="col" className="cit-form__offset-table-actions-col">
                              <span className="cit-form__sr-only">Actions</span>
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {row.lines.map((line, lineIndex) => {
                            const selectOptions = line.item_no.trim()
                              ? Array.from(new Set([line.item_no.trim(), ...itemOptions]))
                              : itemOptions;
                            const canRemoveLine = !rowDisabled && row.lines.length > 1;
                            const lineUom = uomForItemCode(itemUomByCode, line.item_no);
                            return (
                              <tr key={line.id}>
                                <td>
                                  <select
                                    className={inputClass(rowDisabled, 'mono')}
                                    value={line.item_no}
                                    onChange={(e) =>
                                      updateLine(row, line.id, { item_no: e.target.value })
                                    }
                                    disabled={rowDisabled || selectOptions.length === 0}
                                    aria-label={`Deposit item ${lineIndex + 1}`}
                                  >
                                    <option value="">Select from General</option>
                                    {selectOptions.map((code) => (
                                      <option key={code} value={code}>
                                        {code}
                                      </option>
                                    ))}
                                  </select>
                                </td>
                                <td>
                                  <QtyWithUom uom={lineUom}>
                                    <input
                                      className={inputClass(rowDisabled)}
                                      type="number"
                                      min="0"
                                      step="any"
                                      value={line.quantity}
                                      onChange={(e) =>
                                        updateLine(row, line.id, { quantity: e.target.value })
                                      }
                                      disabled={rowDisabled}
                                      placeholder="Qty"
                                      aria-label={`Deposit qty ${lineIndex + 1}`}
                                    />
                                  </QtyWithUom>
                                </td>
                                <td className="cit-form__offset-table-actions-col">
                                  {canRemoveLine ? (
                                    <button
                                      type="button"
                                      className="btn-secondary btn-sm"
                                      onClick={() => removeLine(row, line.id)}
                                    >
                                      Remove
                                    </button>
                                  ) : null}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
    </section>
  );
}

function OffsetPairSection({
  rows,
  itemOptions,
  itemUomByCode,
  offsetOverPaid,
  pendingMap,
  disabled,
  sectionLocked,
  committedIds,
  onRequestEdit,
  onCancelEdit,
  onAdd,
  onChangeRow,
  onRemove,
  onPickFile,
  onClearFile,
  onOpenAttachment,
  onCreateStockTransferForm,
  creatingStockTransferOffsetId,
}: {
  rows: CitOffsetRow[];
  itemOptions: string[];
  itemUomByCode: Record<string, string>;
  offsetOverPaid: { itemNo: string; paidQty: number; offsetQty: number }[];
  pendingMap: Record<string, File | null>;
  disabled?: boolean;
  sectionLocked: boolean;
  committedIds: string[];
  onRequestEdit?: () => void;
  onCancelEdit?: () => void;
  onAdd: () => void;
  onChangeRow: (id: string, partial: Partial<CitOffsetRow>) => void;
  onRemove: (id: string) => void;
  onPickFile: (id: string, file: File | null) => void;
  onClearFile: (id: string) => void;
  onOpenAttachment?: (ref: CitAttachmentRef) => void;
  onCreateStockTransferForm?: (offsetId: string) => void | Promise<void>;
  creatingStockTransferOffsetId?: string | null;
}) {
  const [open, setOpen] = useState(true);

  function updateLine(row: CitOffsetRow, lineId: string, partial: Partial<CitOffsetLine>) {
    onChangeRow(row.id, {
      lines: row.lines.map((line) => (line.id === lineId ? { ...line, ...partial } : line)),
    });
  }

  function sumWarehouseQty(defu: string, depot: string): string {
    if (!defu.trim() && !depot.trim()) return '';
    const a = Number(defu || 0);
    const b = Number(depot || 0);
    if (!Number.isFinite(a) || !Number.isFinite(b)) return '';
    const sum = a + b;
    return Number.isInteger(sum) ? String(sum) : String(Math.round(sum * 1000) / 1000);
  }

  function updateWarehouse(
    row: CitOffsetRow,
    lineId: string,
    partial: Pick<CitOffsetLine, 'warehouse_defu_qty' | 'warehouse_depot_qty'> | {
      warehouse_defu_qty?: string;
      warehouse_depot_qty?: string;
    },
  ) {
    onChangeRow(row.id, {
      lines: row.lines.map((line) => {
        if (line.id !== lineId) return line;
        const next = { ...line, ...partial };
        return {
          ...next,
          quantity: sumWarehouseQty(next.warehouse_defu_qty, next.warehouse_depot_qty),
        };
      }),
    });
  }

  function addLine(row: CitOffsetRow) {
    if (row.lines.length >= 6) return;
    onChangeRow(row.id, { lines: [...row.lines, createCitOffsetLine()] });
  }

  function removeLine(row: CitOffsetRow, lineId: string) {
    if (row.lines.length <= 1) return;
    onChangeRow(row.id, { lines: row.lines.filter((line) => line.id !== lineId) });
  }

  const fileReq = CIT_REQUIRE_OFFSET_SUPPORTING_FILE ? ' *' : '';
  const allowLockedOffsetUpload = CIT_ALLOW_LOCKED_OFFSET_FILE_UPLOAD;

  return (
    <section
      className={`cit-form__panel cit-form__panel--offset${open ? '' : ' is-collapsed'}${
        sectionLocked ? ' is-section-locked' : ''
      }${allowLockedOffsetUpload ? ' cit-form__panel--allow-file-upload' : ''}`}
    >
      <div className="cit-form__panel-head">
        <button
          type="button"
          className="cit-form__panel-toggle"
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
        >
          <span className="cit-form__chevron" aria-hidden>
            {open ? '▾' : '▸'}
          </span>
          <span className="cit-form__panel-heading">
            <span className="cit-form__step-chip" aria-hidden>
              2
            </span>
            <span className="cit-form__panel-heading-text">
              <span className="cit-form__panel-title">Offset invoices</span>
              <span className="cit-form__panel-subtitle">
                Offset against deposit → stock transfer
              </span>
            </span>
          </span>
          {sectionLocked ? <span className="cit-form__lock-pill">Locked</span> : null}
        </button>
        <div className="cit-form__panel-actions">
          {sectionLocked && onRequestEdit ? (
            <button
              type="button"
              className="cit-form__edit-link"
              onClick={() => {
                setOpen(true);
                onRequestEdit();
              }}
            >
              Edit
            </button>
          ) : null}
          {!sectionLocked && onCancelEdit ? (
            <button
              type="button"
              className="cit-form__edit-link"
              onClick={() => {
                setOpen(true);
                onCancelEdit();
              }}
            >
              Cancel edit
            </button>
          ) : null}
          {!disabled ? (
            <button
              type="button"
              className="btn-secondary btn-sm"
              onClick={() => {
                setOpen(true);
                onAdd();
              }}
            >
              + Add pair
            </button>
          ) : null}
        </div>
      </div>
      {open ? (
      <div className="cit-form__panel-body">
        {sectionLocked ? (
          <p className="field-hint cit-form__lock-hint">
            Saved pairs are locked. Add a new pair below, or select Edit to change the latest offset.
            Earlier offsets stay read-only once a newer one exists.
          </p>
        ) : null}
        {itemOptions.length === 0 ? (
          <p className="field-hint">Add item numbers in General first to link offset invoices.</p>
        ) : null}
        {offsetOverPaid.length > 0 ? (
          <div className="alert alert-warn cit-form__offset-over-paid" role="status">
            Offset qty exceeds paid qty for:{' '}
            {offsetOverPaid
              .map(
                (row) =>
                  `${row.itemNo} (offset ${formatCitQty(row.offsetQty)} > paid ${formatCitQty(row.paidQty)})`,
              )
              .join('; ')}
            .
          </div>
        ) : null}
        <div className="cit-form__pair-list">
          {rows.map((row, index) => {
            const isLastOffset = index === rows.length - 1;
            const priorOffsetFrozen = !isLastOffset;
            const rowLocked =
              priorOffsetFrozen || (sectionLocked && isCommitted(row.id, committedIds));
            const rowDisabled = Boolean(disabled || rowLocked);
            const canRemove =
              isLastOffset &&
              !disabled &&
              !row.stock_transfer_form &&
              (!sectionLocked || !isCommitted(row.id, committedIds));
            const hasForm = Boolean(row.stock_transfer_form);
            const canManageStockTransferForm = isLastOffset && Boolean(onCreateStockTransferForm);
            return (
              <div
                key={row.id}
                className={`cit-form__pair-row cit-form__pair-row--offset${
                  rowLocked ? ' is-locked' : ''
                }${allowLockedOffsetUpload ? ' cit-form__pair-row--allow-file-upload' : ''}`}
              >
                <div className="cit-form__offset-top">
                  <div className="cit-form__lane cit-form__lane--primary">
                    <div className="cit-form__pair-half-head">
                      <div className="cit-form__pair-half-title">
                        Offset invoice {rows.length > 1 ? index + 1 : ''}
                      </div>
                      {canRemove ? (
                        <button
                          type="button"
                          className="btn-secondary btn-sm"
                          aria-label="Remove pair"
                          onClick={() => onRemove(row.id)}
                        >
                          Remove pair
                        </button>
                      ) : null}
                    </div>
                    <div className="cit-form__kv-stack">
                      <div className="field cit-form__kv">
                        <span className="field-label">Offset invoice no.{fileReq}</span>
                        <LinkedDocNumber
                          value={row.number}
                          onChange={(number) => onChangeRow(row.id, { number })}
                          disabled={rowDisabled}
                          placeholder="Invoice number"
                          saved={row.file}
                          pending={pendingMap[row.id] ?? null}
                          onPick={(file) => onPickFile(row.id, file)}
                          onClearPending={() => onClearFile(row.id)}
                          uploadDisabled={
                            Boolean(disabled) ||
                            (allowLockedOffsetUpload ? false : rowDisabled)
                          }
                          onOpenAttachment={onOpenAttachment}
                          ariaLabel="Offset invoice number"
                        />
                      </div>
                      <label className="field cit-form__kv">
                        <span className="field-label">Invoice amount</span>
                        <AmountField
                          value={row.invoice_amount}
                          onChange={(invoice_amount) =>
                            onChangeRow(row.id, { invoice_amount })
                          }
                          disabled={rowDisabled}
                        />
                      </label>
                    </div>
                  </div>
                  <div className="cit-form__lane cit-form__lane--secondary">
                    <div className="cit-form__pair-half-head">
                      <div className="cit-form__pair-half-title">Stock transfer</div>
                    </div>
                    <div className="cit-form__kv-stack">
                      <div className="field cit-form__kv cit-form__offset-st-no-field">
                        <span className="field-label">Stock transfer no.</span>
                        {row.stock_transfer_form &&
                        onOpenAttachment &&
                        row.stock_transfer_no.trim() ? (
                          <button
                            type="button"
                            className={`${inputClass(true, 'mono')} cit-form__value-link`}
                            onClick={() => onOpenAttachment(row.stock_transfer_form!)}
                            title={row.stock_transfer_form.fileName}
                            aria-label="Stock transfer number"
                          >
                            {row.stock_transfer_no.trim()}
                          </button>
                        ) : (
                          <ReadonlyValue
                            value={row.stock_transfer_no}
                            placeholder="Auto on save"
                            mono
                          />
                        )}
                      </div>
                      <div className="cit-form__kv">
                        <span className="field-label">Supporting file</span>
                        <div className="cit-form__st-form-actions">
                          {canManageStockTransferForm ? (
                            <button
                              type="button"
                              className="btn-primary btn-sm"
                              disabled={
                                Boolean(disabled) ||
                                !row.stock_transfer_no.trim() ||
                                creatingStockTransferOffsetId === row.id
                              }
                              onClick={() => void onCreateStockTransferForm?.(row.id)}
                            >
                              {creatingStockTransferOffsetId === row.id
                                ? hasForm
                                  ? 'Updating…'
                                  : 'Creating…'
                                : hasForm
                                  ? 'Update form'
                                  : 'Create form'}
                            </button>
                          ) : isLastOffset && !onCreateStockTransferForm ? (
                            <p className="field-hint">Save the entry first to create the form.</p>
                          ) : null}
                          {!row.stock_transfer_form && canManageStockTransferForm ? (
                            <p className="field-hint">No stock transfer form yet.</p>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="cit-form__offset-table-wrap">
                  <div className="cit-form__offset-lines-head">
                    <span className="field-label">Items &amp; warehouse qty</span>
                    {!rowDisabled && row.lines.length < 6 ? (
                      <button
                        type="button"
                        className="btn-secondary btn-sm"
                        onClick={() => addLine(row)}
                      >
                        + Add item
                      </button>
                    ) : null}
                  </div>
                  <div className="cit-form__offset-table-scroll">
                    <table className="cit-form__offset-table">
                      <thead>
                        <tr>
                          <th scope="col">Item</th>
                          <th scope="col">Defu</th>
                          <th scope="col">Depot</th>
                          <th scope="col">Qty</th>
                          <th scope="col" className="cit-form__offset-table-actions-col">
                            <span className="cit-form__sr-only">Actions</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {row.lines.map((line, lineIndex) => {
                          const selectOptions = line.item_no.trim()
                            ? Array.from(new Set([line.item_no.trim(), ...itemOptions]))
                            : itemOptions;
                          const canRemoveLine = !rowDisabled && row.lines.length > 1;
                          const qtyDisplay = sumWarehouseQty(
                            line.warehouse_defu_qty,
                            line.warehouse_depot_qty,
                          );
                          const lineUom = uomForItemCode(itemUomByCode, line.item_no);
                          return (
                            <tr key={line.id}>
                              <td>
                                <select
                                  className={inputClass(rowDisabled, 'mono')}
                                  value={line.item_no}
                                  onChange={(e) =>
                                    updateLine(row, line.id, { item_no: e.target.value })
                                  }
                                  disabled={rowDisabled || selectOptions.length === 0}
                                  aria-label={`Item ${lineIndex + 1}`}
                                >
                                  <option value="">Select from General</option>
                                  {selectOptions.map((code) => (
                                    <option key={code} value={code}>
                                      {code}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td>
                                <input
                                  className={inputClass(rowDisabled)}
                                  type="number"
                                  min="0"
                                  step="any"
                                  value={line.warehouse_defu_qty}
                                  onChange={(e) =>
                                    updateWarehouse(row, line.id, {
                                      warehouse_defu_qty: e.target.value,
                                    })
                                  }
                                  disabled={rowDisabled}
                                  placeholder="0"
                                  aria-label={`Defu qty ${lineIndex + 1}`}
                                />
                              </td>
                              <td>
                                <input
                                  className={inputClass(rowDisabled)}
                                  type="number"
                                  min="0"
                                  step="any"
                                  value={line.warehouse_depot_qty}
                                  onChange={(e) =>
                                    updateWarehouse(row, line.id, {
                                      warehouse_depot_qty: e.target.value,
                                    })
                                  }
                                  disabled={rowDisabled}
                                  placeholder="0"
                                  aria-label={`Depot qty ${lineIndex + 1}`}
                                />
                              </td>
                              <td title="Qty = Defu + Depot">
                                <QtyWithUom uom={lineUom}>
                                  <ReadonlyValue
                                    value={qtyDisplay}
                                    placeholder="0"
                                    className="cit-form__readonly-value--qty"
                                  />
                                </QtyWithUom>
                              </td>
                              <td className="cit-form__offset-table-actions-col">
                                {canRemoveLine ? (
                                  <button
                                    type="button"
                                    className="btn-secondary btn-sm"
                                    onClick={() => removeLine(row, line.id)}
                                  >
                                    Remove
                                  </button>
                                ) : null}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      ) : null}
    </section>
  );
}
